import ky, {
  type AfterResponseHook,
  type HTTPError,
  type BeforeErrorHook,
  type BeforeRequestHook,
  type KyInstance
} from 'ky'
import { type App } from 'vue'
import Keycloak from 'keycloak-js'
import * as Sentry from '@sentry/vue'
import { createSharedRefresh } from './tokenRefresh'

let api: KyInstance

const bearerOf = (request: Request): string | null => request.headers.get('Authorization')

const createApiPlugin = (keycloak: Keycloak) => ({
  install: (app: App): void => {
    // I 401 concorrenti condividono un solo rinnovo; se fallisce, logout una volta sola.
    const refresh = createSharedRefresh(keycloak, () =>
      keycloak.logout({ redirectUri: window.location.origin })
    )

    const beforeRequest: BeforeRequestHook = async (request) => {
      if (keycloak.token) {
        request.headers.set('Authorization', `Bearer ${keycloak.token}`)
      }
      // GET in JSON-LD (le collection si leggono da hydra:member); gli altri metodi in JSON, così
      // anche un errore di una POST multipart arriva come {message} e non come pagina HTML.
      // L'Accept non tocca il Content-Type (multipart, merge-patch+json).
      if (request.method === 'GET') {
        request.headers.set('Accept', 'application/ld+json')
      } else if (!request.headers.has('Accept')) {
        request.headers.set('Accept', 'application/json')
      }
    }

    const beforeError: BeforeErrorHook = async (error) => {
      const { request, response } = error
      const status = response?.status ?? 'unknown'
      const url = request?.url ?? 'unknown'

      console.error(`[API Error] ${status} ${request?.method} ${url}`, error.message)

      Sentry.addBreadcrumb({
        category: 'api',
        message: `${status} ${request?.method} ${url}`,
        level: 'error',
        data: { status, method: request?.method, url }
      })

      // Il corpo di una risposta si legge una volta sola: lo leggo qui su un clone e lo lascio in
      // error.data, così getErrorMessage resta sincrono e response.json() resta usabile dai chiamanti.
      if (response && !(error as HTTPError & { data?: unknown }).data) {
        try {
          ;(error as HTTPError & { data?: unknown }).data = await response.clone().json()
        } catch {
          // corpo vuoto o non JSON: nessun messaggio dell'API da mostrare
        }
      }

      return error
    }

    // 401: rinnova il token (una volta, condiviso) e ritenta la richiesta originale una sola volta.
    // Il retry usa un ky senza questo hook, quindi un secondo 401 non innesca altri giri.
    const afterResponse: AfterResponseHook = async (request, options, response) => {
      if (response.status !== 401) return

      // Se un'altra richiesta ha già rinnovato il token dopo la partenza di questa, basta ritentare.
      const sentWith = bearerOf(request)
      const alreadyRefreshed = !!keycloak.token && sentWith !== `Bearer ${keycloak.token}`
      if (!alreadyRefreshed && !(await refresh())) return

      return ky(request, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
        // Il retry non eredita le opzioni della chiamata originale: senza timeout esplicito
        // le chiamate lunghe (ERP, report: 120 s) scadrebbero al default di ky (10 s).
        timeout: (options as { timeout?: number | false }).timeout,
        hooks: { beforeError: [beforeError] }
      })
    }

    api = ky.create({
      prefixUrl: `${import.meta.env.VITE_API_URL}`,
      hooks: {
        beforeRequest: [beforeRequest],
        afterResponse: [afterResponse],
        beforeError: [beforeError]
      }
    })

    app.config.globalProperties.$api = api
    app.provide('api', api)
  }
})

export default createApiPlugin
export { api }
